import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, nairaFormat, calcEarnings, formatCreativeShare, getGigMinReach } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast, Toaster } from "sonner";
import { Trash2, Plus, ExternalLink, CheckCircle2, Clock, Send, Wallet, Share2, Download } from "lucide-react";
import ProfileBuilder from "@/components/ProfileBuilder";

const STATUS_STYLE = {
  open: "bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40",
  closed: "bg-[#F4B41A]/20 text-[#F4B41A] border-[#F4B41A]/40",
  pending: "bg-[#F4B41A]/20 text-[#F4B41A] border-[#F4B41A]/40",
  selected: "bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40",
  declined: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40",
  rejected: "bg-[#0D2114] text-[#8A9F90] border-[#122E1C]",
  in_progress: "bg-[#F4B41A]/20 text-[#F4B41A] border-[#F4B41A]/40",
  proof_submitted: "bg-[#F4B41A]/20 text-[#F4B41A] border-[#F4B41A]/40",
  verified: "bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40",
  paid: "bg-[#0D2114] text-[#00E676] border-[#00E676]",
};

export default function CreativeDashboard() {
  const { user, refresh } = useAuth();
  const [gigs, setGigs] = useState([]);
  const [apps, setApps] = useState([]);
  const [earnings, setEarnings] = useState({ total_earned_ngn: 0, gigs: [] });

  const loadAll = async () => {
    try {
      const [g, a, e] = await Promise.all([
        api.get("/gigs"),
        api.get("/me/applications"),
        api.get("/me/earnings"),
      ]);
      setGigs(g.data || []);
      setApps(a.data || []);
      setEarnings(e.data || { total_earned_ngn: 0, gigs: [] });
    } catch {}
  };

  useEffect(() => { loadAll(); }, []);

  const myEstimate = calcEarnings(user?.total_followers || 0);

  return (
    <div className="min-h-screen bg-[#030A06] pb-20">
      <Header />
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Profile-incomplete banner */}
        {user && !user.profile_image_path && (
          <div data-testid="profile-incomplete-banner" className="mb-6 border border-[#F4B41A]/40 bg-[#F4B41A]/10 p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <span className="font-mono-jb text-[10px] uppercase tracking-widest text-[#F4B41A]">// Action required</span>
              <p className="mt-1">Add a profile photo to be listed on the public creators directory and unlock gig applications.</p>
            </div>
            <Button
              data-testid="goto-marketplace-from-banner"
              onClick={() => document.querySelector('[data-testid="tab-marketplace"]')?.click()}
              className="bg-[#F4B41A] hover:bg-[#F4B41A]/80 text-[#030A06] font-bold rounded-none"
            >
              Add photo now
            </Button>
          </div>
        )}

        {/* Heading */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <span className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-[#00E676]">// Creative dashboard</span>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">
              Welcome back, <span className="text-[#00E676]">{user?.name?.split(" ")[0]}</span>.
            </h1>
          </div>
          <div className="flex gap-3 flex-wrap">
            <KPI label="Total followers" value={(user?.total_followers || 0).toLocaleString()} />
            <KPI label="Lifetime earned" value={nairaFormat(earnings.total_earned_ngn)} accent />
            <KPI label="Open gigs" value={gigs.filter((g) => g.status === "open").length} />
          </div>
        </div>

        <Tabs defaultValue="gigs" className="w-full">
          <div className="overflow-x-auto mb-8 -mx-6 px-6 md:mx-0 md:px-0">
            <TabsList data-testid="creative-tabs" className="bg-transparent border border-[#122E1C] rounded-none h-auto p-0 inline-flex w-max">
              {["gigs", "applications", "earnings", "marketplace", "profile"].map((t) => (
                <TabsTrigger
                  key={t}
                  value={t}
                  data-testid={`tab-${t}`}
                  className="rounded-none data-[state=active]:bg-[#00E676] data-[state=active]:text-[#030A06] text-[#8A9F90] text-xs uppercase tracking-[0.2em] font-mono-jb px-4 md:px-6 py-3 whitespace-nowrap"
                >
                  {t}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* GIGS */}
          <TabsContent value="gigs" className="space-y-4">
            {gigs.length === 0 && <Empty msg="No gigs yet. Admin will post briefs here as clients book them." />}
            {gigs.map((g) => (
              <GigRow key={g.id} gig={g} apps={apps} onChanged={loadAll} userId={user.id} userFollowers={user.total_followers} />
            ))}
          </TabsContent>

          {/* APPLICATIONS */}
          <TabsContent value="applications" className="space-y-3">
            {apps.length === 0 && <Empty msg="You haven't applied to any gigs yet." />}
            {apps.map((a) => (
              <div key={a.id} data-testid={`application-row-${a.id}`} className="border border-[#122E1C] bg-[#07140B] p-5 flex items-center justify-between lift">
                <div>
                  <div className="font-display font-bold text-lg tracking-tight">{a.gig?.title}</div>
                  <div className="text-xs text-[#8A9F90] font-mono-jb mt-1">Applied {new Date(a.applied_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono-jb text-[#00E676]">{nairaFormat(Math.round((a.gig?.budget_ngn || 0) * 0.8))}</span>
                  <Badge className={`${STATUS_STYLE[a.status] || "bg-[#0D2114] text-[#8A9F90]"} border rounded-none uppercase tracking-widest text-[10px] font-mono-jb`}>{a.status}</Badge>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* EARNINGS */}
          <TabsContent value="earnings" className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="border border-[#00E676]/40 bg-[#0D2114] p-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Lifetime earned</div>
                <div data-testid="earnings-lifetime" className="font-mono-jb text-4xl text-[#00E676] mt-2 font-bold">{nairaFormat(earnings.total_earned_ngn)}</div>
              </div>
              <div className="border border-[#122E1C] bg-[#07140B] p-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Estimated per 10K-gig</div>
                <div className="font-mono-jb text-2xl text-white mt-2">{nairaFormat(calcEarnings(10000).creative)}</div>
              </div>
              <div className="border border-[#122E1C] bg-[#07140B] p-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Estimated per your reach</div>
                <div className="font-mono-jb text-2xl text-[#F4B41A] mt-2">{nairaFormat(myEstimate.creative)}</div>
              </div>
            </div>

            <div className="border border-[#122E1C]">
              <div className="grid grid-cols-5 bg-[#0D2114] text-[10px] uppercase tracking-[0.2em] font-mono-jb text-[#8A9F90]">
                <div className="p-4 col-span-2">Gig</div>
                <div className="p-4">Date paid</div>
                <div className="p-4 text-[#00E676] text-right">Amount</div>
                <div className="p-4 text-right">Share</div>
              </div>
              {earnings.gigs.length === 0 && <div className="p-6 text-[#8A9F90] text-sm text-center">No payouts yet.</div>}
              {earnings.gigs.map((g) => (
                <div key={g.id} data-testid={`earning-row-${g.id}`} className="grid grid-cols-5 border-t border-[#122E1C] hover:bg-[#0D2114]/40 items-center">
                  <div className="p-4 col-span-2 truncate">{g.title}</div>
                  <div className="p-4 font-mono-jb text-sm text-[#8A9F90]">{g.payout_marked_at ? new Date(g.payout_marked_at).toLocaleDateString() : "—"}</div>
                  <div className="p-4 font-mono-jb text-[#00E676] text-right">{nairaFormat(g.creative_share_ngn)}</div>
                  <div className="p-4 text-right">
                    <ShareEarnings gig={g} creativeName={user?.name} />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* MARKETPLACE PROFILE */}
          <TabsContent value="marketplace">
            <ProfileBuilder user={user} onSaved={refresh} />
          </TabsContent>

          {/* PROFILE / AUDIT */}
          <TabsContent value="profile">
            <ProfileEditor user={user} onSaved={async () => { await refresh(); toast.success("Profile updated"); }} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className="border border-[#122E1C] px-5 py-3 bg-[#07140B]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">{label}</div>
      <div className={`font-mono-jb text-xl mt-1 ${accent ? "text-[#00E676]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="border border-dashed border-[#122E1C] p-12 text-center text-[#8A9F90] text-sm" data-testid="empty-state">{msg}</div>;
}

const HINT_TONE = {
  eligible: "bg-[#00E676]/10 text-[#00E676] border-[#00E676]/40",
  ineligible: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40",
  info: "bg-[#F4B41A]/10 text-[#F4B41A] border-[#F4B41A]/40",
  neutral: "bg-[#0D2114] text-[#8A9F90] border-[#122E1C]",
  success: "bg-[#00E676]/20 text-[#00E676] border-[#00E676]",
};

function buildEligibilityHint({ gig, myStatus, accepted, declined, isSelected, meetsReach, proofSubmitted, isAcceptingApplications, userFollowers, effectiveMinReach }) {
  // Terminal / lifecycle states first
  if (myStatus === "paid") {
    return { tone: "success", title: "Paid", body: "Payout completed. Check your Earnings tab for the amount." };
  }
  if (myStatus === "rejected") {
    return { tone: "neutral", title: "Not selected", body: "Admin picked another creative for this brief. Keep an eye out — more gigs land regularly." };
  }
  if (isSelected && proofSubmitted) {
    return { tone: "info", title: "Awaiting admin verification", body: "Your proof of work is in review. Payout will follow within 24–48h once verified." };
  }
  if (isSelected && !proofSubmitted) {
    return { tone: "info", title: "You've been selected", body: "Deliver the work, then submit your live links using the button on the right." };
  }
  if (accepted) {
    return { tone: "info", title: "Deal accepted", body: "You're in the pool. Admin will pick the best fit from all applicants." };
  }
  if (declined) {
    return { tone: "neutral", title: "You declined this deal", body: "Changed your mind? Tap the Accept link on the right to jump back in." };
  }
  if (!isAcceptingApplications) {
    return { tone: "neutral", title: gig.status === "paid" ? "Gig fully paid out" : "Gig closed", body: "This gig is no longer accepting applications." };
  }
  // Open, no application yet
  if (gig.is_special) {
    return { tone: "eligible", title: "★ Open to all creatives", body: "No follower minimum — every registered creative can accept this special gig." };
  }
  const need = Number(effectiveMinReach || 0);
  if (meetsReach) {
    return {
      tone: "eligible",
      title: "You're eligible",
      body: `Your ${userFollowers.toLocaleString()} followers meet the ${need.toLocaleString()}+ minimum. Accept the deal to enter the applicant pool.`,
    };
  }
  const gap = Math.max(0, need - userFollowers);
  return {
    tone: "ineligible",
    title: "Reach too low",
    body: `You have ${userFollowers.toLocaleString()} followers — this gig needs ${need.toLocaleString()}+. Grow ${gap.toLocaleString()} more to unlock, or wait for a lower-reach brief.`,
  };
}

function GigRow({ gig, apps, userId, userFollowers, onChanged }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [proofLinks, setProofLinks] = useState([""]);
  const [busy, setBusy] = useState(false);

  // Prefer server-attached my_status; fall back to apps lookup
  const myApp = apps.find((a) => a.gig_id === gig.id) || {
    status: gig.my_status,
    proof_links: gig.my_proof_links || [],
    proof_submitted_at: gig.my_proof_submitted_at,
  };
  const myStatus = gig.my_status || myApp?.status || null;
  const accepted = myStatus === "pending" || myStatus === "selected";
  const declined = myStatus === "declined";
  const isSelected = myStatus === "selected";
  // Effective minimum reach = label-derived (source-of-truth for what the creative sees).
  // Protects against admin data-entry drift where required_followers and follower_range_label
  // were edited independently in the Edit Gig form.
  const effectiveMinReach = getGigMinReach(gig);
  const meetsReach = gig.is_special || userFollowers >= effectiveMinReach;
  // Gig is still accepting applications until admin explicitly closes it or marks all paid.
  // This ensures unlimited creatives can Accept Deal until the deadline / manual close.
  const isAcceptingApplications = !["paid", "closed"].includes(gig.status);
  const proofSubmitted = (myApp?.proof_links?.length || 0) > 0;
  const eligibilityHint = buildEligibilityHint({
    gig, myStatus, accepted, declined, isSelected, meetsReach,
    proofSubmitted, isAcceptingApplications,
    userFollowers: userFollowers || 0,
    effectiveMinReach,
  });

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/gigs/${gig.id}/apply`, { message });
      toast.success("Deal accepted · admin notified");
      setOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!window.confirm("Decline this deal? It will be removed from your feed.")) return;
    setBusy(true);
    try {
      await api.post(`/gigs/${gig.id}/decline`);
      toast("Deal declined", { description: "Gig hidden from your feed. Admin notified." });
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submitProof = async () => {
    const links = proofLinks.map((l) => l.trim()).filter(Boolean);
    if (links.length === 0) return toast.error("Add at least one link");
    setBusy(true);
    try {
      await api.post(`/gigs/${gig.id}/proof`, { proof_links: links });
      toast.success("Proof submitted. Admin will verify within 24–48h.");
      setOpen(false);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`gig-card-${gig.id}`} className="border border-[#122E1C] bg-[#07140B] p-6 lift">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`${STATUS_STYLE[gig.status]} border rounded-none uppercase tracking-widest text-[10px] font-mono-jb`}>{gig.status.replace("_", " ")}</Badge>
            {gig.is_special && <Badge className="bg-[#F4B41A]/20 text-[#F4B41A] border border-[#F4B41A]/40 rounded-none uppercase tracking-widest text-[10px] font-mono-jb">★ Special</Badge>}
            {gig.client_name && <span className="text-xs text-[#8A9F90]">· {gig.client_name}</span>}
          </div>
          <h3 className="font-display font-bold text-2xl tracking-tight">{gig.title}</h3>
          <p className="text-sm text-[#8A9F90] mt-2 max-w-2xl">{gig.brief}</p>
          <div className="flex gap-6 mt-4 text-xs font-mono-jb text-[#8A9F90]">
            {gig.is_special ? (
              <span><span className="text-[#F4B41A]">OPEN TO:</span> All registered creatives — no follower minimum</span>
            ) : (
              <span><span className="text-[#F4B41A]">REACH:</span> {gig.follower_range_label || `${gig.required_followers?.toLocaleString()}+ followers`}</span>
            )}
            {gig.platforms?.length > 0 && <span><span className="text-[#F4B41A]">ON:</span> {gig.platforms.join(", ")}</span>}
            {gig.deadline && <span><span className="text-[#F4B41A]">BY:</span> {gig.deadline}</span>}
          </div>

          {/* Eligibility / status hint strip */}
          <div
            data-testid={`eligibility-hint-${gig.id}`}
            data-tone={eligibilityHint.tone}
            className={`mt-4 border ${HINT_TONE[eligibilityHint.tone]} rounded-none px-3 py-2 max-w-2xl`}
          >
            <div className="font-mono-jb text-[10px] uppercase tracking-[0.2em]">// {eligibilityHint.title}</div>
            <p className="text-xs mt-1 leading-relaxed opacity-90">{eligibilityHint.body}</p>
          </div>
        </div>

        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">You take home</div>
          <div className="font-mono-jb text-3xl text-[#00E676] font-bold">{formatCreativeShare(gig)}</div>
          <div className="text-[10px] text-[#8A9F90] font-mono-jb mt-1">Final amount set by admin after selection</div>

          <div className="mt-4">
            {isAcceptingApplications && !accepted && !declined && (
              <div className="flex flex-col gap-2 items-end">
                <div className="flex gap-2 flex-wrap justify-end">
                  <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                      <Button
                        data-testid={`accept-deal-btn-${gig.id}`}
                        disabled={!meetsReach}
                        className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none disabled:opacity-40"
                      >
                        Accept Deal
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#07140B] border-[#122E1C] text-white rounded-none max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
                      <DialogHeader>
                        <DialogTitle className="font-display tracking-tight">Accept Deal: {gig.title}</DialogTitle>
                      </DialogHeader>
                      <p className="text-sm text-[#8A9F90]">By accepting, you're telling admin you're ready to deliver. Add a quick pitch (optional) and admin will pick the best fit.</p>
                      <Textarea data-testid="apply-message" placeholder="Why you're the right fit (optional)" value={message} onChange={(e) => setMessage(e.target.value)} className="bg-transparent border-[#122E1C] rounded-none text-white" />
                      <DialogFooter>
                        <Button data-testid="apply-confirm-btn" disabled={busy} onClick={accept} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
                          Confirm Accept
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <Button
                    data-testid={`decline-deal-btn-${gig.id}`}
                    onClick={decline}
                    disabled={busy}
                    variant="outline"
                    className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] rounded-none"
                  >
                    Decline
                  </Button>
                </div>
              </div>
            )}

            {isAcceptingApplications && accepted && (
              <div className="flex flex-col gap-2 items-end">
                <Badge data-testid={`accepted-badge-${gig.id}`} className="bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40 rounded-none uppercase font-mono-jb">
                  Deal Accepted
                </Badge>
              </div>
            )}

            {isAcceptingApplications && declined && (
              <div className="flex flex-col gap-2 items-end">
                <Badge data-testid={`declined-badge-${gig.id}`} className="bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/40 rounded-none uppercase font-mono-jb">
                  Declined
                </Badge>
                <button onClick={accept} data-testid={`undo-decline-${gig.id}`} className="text-[10px] text-[#00E676] hover:underline font-mono-jb">Changed your mind? Accept →</button>
              </div>
            )}

            {isSelected && !myApp?.proof_links?.length && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid={`submit-proof-btn-${gig.id}`} className="bg-[#F4B41A] hover:bg-[#F4B41A]/80 text-[#030A06] font-bold rounded-none">
                    Submit proof of work
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#07140B] border-[#122E1C] text-white rounded-none max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
                  <DialogHeader>
                    <DialogTitle className="font-display tracking-tight">Submit live links</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-2">
                    {proofLinks.map((l, i) => (
                      <div key={i} className="flex gap-2">
                        <Input data-testid={`proof-link-${i}`} placeholder="https://tiktok.com/@you/video/…" value={l} onChange={(e) => { const n = [...proofLinks]; n[i] = e.target.value; setProofLinks(n); }} className="bg-transparent border-[#122E1C] rounded-none text-white" />
                        {proofLinks.length > 1 && <Button type="button" variant="outline" onClick={() => setProofLinks(proofLinks.filter((_, j) => j !== i))} className="border-[#122E1C] rounded-none"><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    ))}
                    <Button type="button" variant="ghost" onClick={() => setProofLinks([...proofLinks, ""])} data-testid="add-proof-link" className="text-[#00E676] hover:bg-[#0D2114]"><Plus className="w-4 h-4 mr-1" /> Add link</Button>
                  </div>
                  <DialogFooter>
                    <Button data-testid="proof-submit-btn" disabled={busy} onClick={submitProof} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">Submit proof</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {isSelected && myApp?.proof_links?.length > 0 && myApp?.status !== "paid" && (
              <Badge data-testid={`pending-badge-${gig.id}`} className="bg-[#F4B41A]/20 text-[#F4B41A] border border-[#F4B41A]/40 rounded-none uppercase font-mono-jb"><Clock className="w-3 h-3 mr-1" /> Awaiting verify</Badge>
            )}

            {false && (
              <Badge data-testid={`verified-badge-${gig.id}`} className="bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40 rounded-none uppercase font-mono-jb"><CheckCircle2 className="w-3 h-3 mr-1" /> Payout coming</Badge>
            )}

            {myApp?.status === "paid" && (
              <Badge data-testid={`paid-badge-${gig.id}`} className="bg-[#00E676]/30 text-[#00E676] border border-[#00E676] rounded-none uppercase font-mono-jb"><Wallet className="w-3 h-3 mr-1" /> Paid</Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileEditor({ user, onSaved }) {
  const [handles, setHandles] = useState(user?.social_handles?.length ? user.social_handles : [{ platform: "instagram", handle: "", followers: 0, url: "" }]);
  const [bank, setBank] = useState(user?.bank_details || { account_name: "", account_number: "", bank_name: "" });
  const [busy, setBusy] = useState(false);

  const total = handles.reduce((s, h) => s + Number(h.followers || 0), 0);

  const saveFollowers = async () => {
    setBusy(true);
    try {
      await api.post("/me/followers", { social_handles: handles.map((h) => ({ ...h, followers: Math.max(0, Math.floor(Number(h.followers) || 0)) })) });
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const saveBank = async () => {
    setBusy(true);
    try {
      await api.put("/me/bank", bank);
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="border border-[#122E1C] bg-[#07140B] p-6">
        <h3 className="font-display font-bold text-2xl tracking-tight mb-2">Social handles & follower audit</h3>
        <p className="text-xs text-[#8A9F90] mb-4">Update on the 1st of each month. Admin may re-verify before posting new gigs.</p>
        <div className="space-y-2">
          {handles.map((h, i) => (
            <div key={i} data-testid={`audit-row-${i}`} className="grid grid-cols-12 gap-2 border border-[#122E1C] p-3">
              <select value={h.platform} onChange={(e) => { const n = [...handles]; n[i] = { ...n[i], platform: e.target.value }; setHandles(n); }} data-testid={`audit-platform-${i}`} className="col-span-3 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none">
                <option value="tiktok">TikTok</option>
                <option value="instagram">Instagram</option>
                <option value="x">X</option>
                <option value="youtube">YouTube</option>
                <option value="facebook">Facebook</option>
              </select>
              <input data-testid={`audit-handle-${i}`} placeholder="@handle" value={h.handle} onChange={(e) => { const n = [...handles]; n[i] = { ...n[i], handle: e.target.value }; setHandles(n); }} className="col-span-4 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none" />
              <input data-testid={`audit-followers-${i}`} type="number" min="0" step="1" inputMode="numeric" pattern="[0-9]*" placeholder="Followers" value={h.followers} onChange={(e) => { const n = [...handles]; n[i] = { ...n[i], followers: e.target.value.replace(/[^0-9]/g, "") }; setHandles(n); }} className="col-span-3 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm font-mono-jb rounded-none" />
              <button onClick={() => setHandles(handles.filter((_, j) => j !== i))} className="col-span-2 text-[#FF3B30] flex justify-center" data-testid={`audit-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <Button type="button" variant="ghost" onClick={() => setHandles([...handles, { platform: "tiktok", handle: "", followers: 0, url: "" }])} className="text-[#00E676] hover:bg-[#0D2114]" data-testid="audit-add">
            <Plus className="w-4 h-4 mr-1" /> Add handle
          </Button>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="font-mono-jb text-sm text-[#8A9F90]">Total: <span className="text-[#00E676] text-base">{total.toLocaleString()}</span> followers</div>
          <Button data-testid="audit-save" disabled={busy} onClick={saveFollowers} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">Save audit</Button>
        </div>
      </div>

      <div className="border border-[#122E1C] bg-[#07140B] p-6">
        <h3 className="font-display font-bold text-2xl tracking-tight mb-2">Bank details</h3>
        <p className="text-xs text-[#8A9F90] mb-4">Only Nasarawa Expo admin can see this. Used for the 80% payout.</p>
        <div className="space-y-3">
          <Input data-testid="bank-account-name" placeholder="Account name" value={bank.account_name} onChange={(e) => setBank({ ...bank, account_name: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none text-white" />
          <Input data-testid="bank-account-number" placeholder="Account number" value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none text-white" />
          <Input data-testid="bank-name" placeholder="Bank" value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none text-white" />
        </div>
        <Button data-testid="bank-save" disabled={busy} onClick={saveBank} className="mt-4 bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">Save bank details</Button>
      </div>
    </div>
  );
}

function ShareEarnings({ gig, creativeName }) {
  const [open, setOpen] = useState(false);
  const cardRef = React.useRef(null);
  const amount = nairaFormat(gig.creative_share_ngn);
  const shareText = `I just earned ${amount} from Nasarawa Expo Marketplace! Nigerian creators, get on the roster: https://nasexpo.com`;

  const downloadPng = async () => {
    // Render the card as canvas → PNG via native APIs, no external libs
    const el = cardRef.current;
    if (!el) return;
    // Convert SVG string to blob → image → canvas
    const w = 1080, h = 1080;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Draw background
    ctx.fillStyle = "#030A06"; ctx.fillRect(0, 0, w, h);
    // Grid lines
    ctx.strokeStyle = "#122E1C"; ctx.lineWidth = 1;
    for (let y = 0; y < h; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
    // Green accent square (bottom right)
    ctx.fillStyle = "#00E676"; ctx.fillRect(w - 240, h - 240, 200, 200);
    // Yellow accent line
    ctx.fillStyle = "#F4B41A"; ctx.fillRect(80, 180, 60, 6);
    // Text
    ctx.fillStyle = "#8A9F90"; ctx.font = "600 22px 'Manrope', sans-serif";
    ctx.fillText("// I JUST EARNED FROM", 80, 220);
    ctx.fillStyle = "#00E676"; ctx.font = "900 60px 'Manrope', sans-serif";
    ctx.fillText("NASARAWA EXPO", 80, 300);
    ctx.fillStyle = "#8A9F90"; ctx.font = "600 30px 'Manrope', sans-serif";
    ctx.fillText("Marketplace", 80, 350);

    ctx.fillStyle = "#FFFFFF"; ctx.font = "900 160px 'Manrope', sans-serif";
    ctx.fillText(amount, 80, 560);
    ctx.fillStyle = "#8A9F90"; ctx.font = "500 28px 'Manrope', sans-serif";
    ctx.fillText(`for "${(gig.title || "").slice(0, 46)}"`, 80, 610);

    ctx.fillStyle = "#F4B41A"; ctx.font = "900 44px 'Manrope', sans-serif";
    ctx.fillText(creativeName || "Nasarawa Creator", 80, 780);

    ctx.fillStyle = "#8A9F90"; ctx.font = "600 22px 'Manrope', sans-serif";
    ctx.fillText("Get on the roster · nasexpo.com", 80, 830);

    ctx.fillStyle = "#00E676"; ctx.font = "900 26px 'Manrope', sans-serif";
    ctx.fillText("80% TO CREATORS · PAID IN 24-48H", 80, h - 80);

    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url; a.download = `nasarawa-earnings-${gig.id}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Share card downloaded");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText, url: "https://nasexpo.com" });
      } catch {}
    } else {
      await navigator.clipboard.writeText(shareText);
      toast.success("Copied to clipboard");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`share-earning-${gig.id}`} className="text-[#00E676] hover:bg-[#00E676]/10 p-2 inline-flex items-center gap-1 text-xs font-mono-jb">
          <Share2 className="w-4 h-4" /> Share
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#07140B] border-[#122E1C] text-white rounded-none max-w-md max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Share your win</DialogTitle>
        </DialogHeader>
        <div ref={cardRef} className="relative bg-[#030A06] border border-[#122E1C] p-6 aspect-square overflow-hidden">
          <div className="absolute bottom-4 right-4 w-24 h-24 bg-[#00E676]" />
          <div className="w-12 h-1 bg-[#F4B41A] mb-4" />
          <div className="font-mono-jb text-[10px] uppercase tracking-widest text-[#8A9F90]">// I JUST EARNED FROM</div>
          <div className="font-display font-black text-2xl text-[#00E676] mt-1 tracking-tighter">NASARAWA EXPO</div>
          <div className="font-mono-jb text-xs text-[#8A9F90]">Marketplace</div>
          <div className="mt-6">
            <div className="font-display font-black text-5xl text-white tracking-tighter">{amount}</div>
            <div className="text-xs text-[#8A9F90] mt-1 truncate">for "{gig.title}"</div>
          </div>
          <div className="mt-6">
            <div className="font-display font-black text-xl text-[#F4B41A] tracking-tight">{creativeName || "Nasarawa Creator"}</div>
            <div className="text-[10px] text-[#8A9F90] font-mono-jb mt-1">Get on the roster · nasexpo.com</div>
          </div>
          <div className="absolute bottom-4 left-4 font-mono-jb text-[10px] text-[#00E676]">80% TO CREATORS · PAID IN 24-48H</div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button data-testid="share-native" onClick={nativeShare} variant="outline" className="border-[#122E1C] hover:bg-[#0D2114] text-white rounded-none">
            <Share2 className="w-4 h-4 mr-1" /> Share text
          </Button>
          <Button data-testid="share-download-png" onClick={downloadPng} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
            <Download className="w-4 h-4 mr-1" /> Download PNG
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

